/**
 * What's wrong with a credential, and how this page should be grouped.
 *
 * Both halves are pure so they can be tested without a DOM, and so the page
 * stays a rendering concern.
 *
 * The grouping half is the answer to "how does a distributor see this versus an
 * MSP versus a company". The old page made everyone walk the org tree through
 * navigation columns before showing them anything -- which is work only the
 * levels *above* you should ever do, and even they had already done it in the
 * global scope bar. Here the hierarchy is a grouping axis instead: you see
 * everything you own, grouped by the level immediately beneath you, and the
 * scope bar still narrows when you want less.
 */
import type { Credential } from "@/lib/api/credentials";
import { Role } from "@/lib/constants/roles";

/**
 * Why a credential needs a human, worst first.
 *
 * Ordered deliberately: a credential can be several of these at once (an
 * expired one has also stopped syncing), and the list is scanned top-down so
 * the first match is the one worth saying. Reporting "never synced" for an
 * account whose secret expired would describe the symptom and hide the cause.
 */
export type AttentionState =
  | "expired"
  | "invalid"
  | "expiring"
  | "pending"
  | "never_synced"
  | "no_currency"
  | "ok";

/** Days before a secret expires that we start warning. */
export const EXPIRY_WARNING_DAYS = 30;

/**
 * Azure service-principal secrets expire, and the date is on the wire already
 * (`azure_credential.expiration_date`). Nothing surfaced it, so the first
 * anyone knew of an expiry was cost data silently stopping -- which reads as
 * "this account spent nothing", the same failure this platform keeps meeting.
 *
 * Returns null when the provider has no expiry concept or the date is absent.
 */
export function daysUntilExpiry(cred: Credential, now: Date = new Date()): number | null {
  const raw = cred.azure_credential?.expiration_date;
  if (!raw) return null;
  const expires = new Date(raw);
  if (Number.isNaN(expires.getTime())) return null;
  // Whole days, floored: "expires in 0 days" means today, not "already gone".
  const ms = expires.getTime() - now.getTime();
  return Math.floor(ms / 86_400_000);
}

export function attentionState(cred: Credential, now: Date = new Date()): AttentionState {
  if (cred.status === "expired" || cred.is_expired) return "expired";
  if (cred.status === "invalid") return "invalid";

  const days = daysUntilExpiry(cred, now);
  if (days !== null && days <= EXPIRY_WARNING_DAYS) {
    // A date in the past with the status not yet flipped: the credential is
    // expired in fact even though nothing has re-validated it to find out.
    return days < 0 ? "expired" : "expiring";
  }

  if (cred.status === "pending") return "pending";
  if (!cred.last_cost_sync) return "never_synced";
  // Last, and only for otherwise-healthy accounts: a missing currency does not
  // stop anything working, it just means every converted total from this
  // account rests on a default rather than on the provider's own word.
  if (!cred.currency) return "no_currency";
  return "ok";
}

/** Does this state mean someone has to do something? */
export function needsAttention(state: AttentionState): boolean {
  return state === "expired" || state === "invalid" || state === "expiring";
}

export interface HealthSummary {
  total: number;
  attention: number;
  neverSynced: number;
  pending: number;
  healthy: number;
}

export function summarize(creds: Credential[], now: Date = new Date()): HealthSummary {
  let attention = 0;
  let neverSynced = 0;
  let pending = 0;
  let healthy = 0;
  for (const c of creds) {
    const s = attentionState(c, now);
    if (needsAttention(s)) attention += 1;
    else if (s === "pending") pending += 1;
    else if (s === "never_synced") neverSynced += 1;
    else healthy += 1;
  }
  return { total: creds.length, attention, neverSynced, pending, healthy };
}

/**
 * Which org level this viewer's credentials are grouped by.
 *
 * One level below where the viewer sits. A company user owns one company, so
 * grouping by it would put every row under a single heading that says what the
 * scope bar already says -- `none` renders a flat list instead.
 */
export type GroupAxis = "msp" | "company" | "none";

export function groupAxisForRole(role: string | undefined): GroupAxis {
  switch (role) {
    case Role.PLATFORM_ADMIN:
    case Role.PLATFORM_MANAGER:
    case Role.DISTRIBUTOR_ADMIN:
      return "msp";
    case Role.MSP_ADMIN:
      return "company";
    default:
      return "none";
  }
}

export interface CredentialGroup {
  key: string;
  label: string;
  credentials: Credential[];
  summary: HealthSummary;
}

/**
 * Group credentials for display, worst-first within and across groups.
 *
 * `companyLabel`/`mspLabel` resolve an org id to a name; an id with no name
 * yet groups under "Unknown" rather than vanishing, because a credential you
 * cannot place is exactly the one worth seeing.
 */
export function groupCredentials(
  creds: Credential[],
  axis: GroupAxis,
  labels: {
    companyName: (id: string) => string | undefined;
    mspForCompany: (id: string) => { id: string; name: string } | undefined;
  },
  now: Date = new Date(),
): CredentialGroup[] {
  const severity = (c: Credential) => {
    const order: AttentionState[] = [
      "expired",
      "invalid",
      "expiring",
      "pending",
      "never_synced",
      "no_currency",
      "ok",
    ];
    return order.indexOf(attentionState(c, now));
  };
  const bySeverityThenName = (a: Credential, b: Credential) =>
    severity(a) - severity(b) || a.nickname.localeCompare(b.nickname);

  if (axis === "none") {
    const sorted = [...creds].sort(bySeverityThenName);
    return sorted.length
      ? [
          {
            key: "all",
            label: "",
            credentials: sorted,
            summary: summarize(sorted, now),
          },
        ]
      : [];
  }

  const buckets = new Map<string, { label: string; items: Credential[] }>();
  for (const c of creds) {
    let key: string;
    let label: string;
    if (axis === "company") {
      key = c.company ?? "unknown";
      label = labels.companyName(c.company) ?? "Unknown company";
    } else {
      const msp = labels.mspForCompany(c.company);
      key = msp?.id ?? "unknown";
      label = msp?.name ?? "Unknown MSP";
    }
    const bucket = buckets.get(key) ?? { label, items: [] };
    bucket.items.push(c);
    buckets.set(key, bucket);
  }

  return [...buckets.entries()]
    .map(([key, { label, items }]) => ({
      key,
      label,
      credentials: [...items].sort(bySeverityThenName),
      summary: summarize(items, now),
    }))
    // Groups with something wrong float up; the rest stay alphabetical so a
    // healthy list does not reorder itself between visits.
    .sort(
      (a, b) =>
        (b.summary.attention > 0 ? 1 : 0) - (a.summary.attention > 0 ? 1 : 0) ||
        a.label.localeCompare(b.label),
    );
}
